import { Link } from '@tanstack/react-router';
import { CodeBlock, Callout } from '../../components';

export default function Content() {
  return (
    <>
      <p>
        To load a GLB in three.js, import <code>GLTFLoader</code> from <code>three/addons/loaders/GLTFLoader.js</code>,
        call <code>await loader.loadAsync(&apos;model.glb&apos;)</code> and add <code>gltf.scene</code> to your scene. If
        the file uses Draco, Meshopt or KTX2 compression, register the matching decoder on the loader first. In React Three
        Fiber, <code>useGLTF</code> from <code>@react-three/drei</code> handles loading, caching and the Draco and Meshopt
        setup in one line, and{' '}
        <strong>gltfjsx</strong> turns the model into a reusable JSX component.
      </p>
      <p>
        This tutorial covers the plain three.js setup, the decoders for compressed models, progress and error handling, the
        errors people actually run into, React Three Fiber with gltfjsx, and how to keep models fast. Code targets three.js
        r186, drei 10 and gltfjsx 6.5.
      </p>

      <h2>Load a GLB with GLTFLoader</h2>
      <p>
        Put the .glb file somewhere your web server can serve it (for Vite, the <code>public/</code> folder), then:
      </p>
      <CodeBlock language="javascript">{`import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

const scene = new THREE.Scene();
const loader = new GLTFLoader();

const gltf = await loader.loadAsync('/models/chair.glb');
scene.add(gltf.scene);`}</CodeBlock>
      <p>
        <code>three/addons/</code> is an alias for <code>three/examples/jsm/</code> in the three package. Without a bundler,
        map both <code>three</code> and <code>three/addons/</code> in an import map, as the three.js installation docs show.
      </p>
      <p>
        The resolved <code>gltf</code> object contains <code>scene</code> (the default scene), <code>scenes</code>,{' '}
        <code>animations</code> (an array of <code>AnimationClip</code>), <code>cameras</code>, <code>asset</code>{' '}
        (generator and version info), <code>parser</code> and <code>userData</code>. To play animations:
      </p>
      <CodeBlock language="javascript">{`const mixer = new THREE.AnimationMixer(gltf.scene);
for (const clip of gltf.animations) {
  mixer.clipAction(clip).play();
}

const timer = new THREE.Timer(); // replaces THREE.Clock, deprecated since r183
renderer.setAnimationLoop((time) => {
  timer.update(time);
  mixer.update(timer.getDelta());
  renderer.render(scene, camera);
});`}</CodeBlock>
      <p>
        glTF materials are physically based, so a model with no lights and no environment map renders black. Add a light
        or set <code>scene.environment</code>.
      </p>

      <h2>Load compressed models: Draco, KTX2 and Meshopt</h2>
      <p>
        GLTFLoader does not bundle any decoders. If the file needs one you haven&apos;t registered, loading fails. Not sure
        what your file uses? The free <Link to="/gltf-validator">glTF validator</Link> lists the extensions in it:{' '}
        <code>KHR_draco_mesh_compression</code>, <code>EXT_meshopt_compression</code> or{' '}
        <code>KHR_texture_basisu</code> (KTX2). A loader that handles all three:
      </p>
      <CodeBlock language="javascript">{`import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';

const dracoLoader = new DRACOLoader().setDecoderPath('/draco/');

const ktx2Loader = new KTX2Loader()
  .setTranscoderPath('/basis/')
  .detectSupport(renderer); // your THREE.WebGLRenderer

const loader = new GLTFLoader()
  .setDRACOLoader(dracoLoader)
  .setKTX2Loader(ktx2Loader)
  .setMeshoptDecoder(MeshoptDecoder);`}</CodeBlock>
      <p>
        Create these once and reuse them for every model. The Draco decoder and Basis transcoder are only fetched when a
        file needs them; the Meshopt decoder is part of your bundle, but it is small.
      </p>

      <h3>DRACOLoader and the decoder path</h3>
      <p>
        <code>setDecoderPath</code> takes a folder URL that contains <code>draco_wasm_wrapper.js</code>,{' '}
        <code>draco_decoder.wasm</code> and <code>draco_decoder.js</code>. three.js appends the file names to the string
        as-is, so the trailing slash is required. You have three options:
      </p>
      <ul>
        <li>
          <strong>Self-host (recommended).</strong> Copy <code>node_modules/three/examples/jsm/libs/draco/</code> into your
          public folder, for example <code>cp -r node_modules/three/examples/jsm/libs/draco public/draco</code>. Its{' '}
          <code>gltf/</code> subfolder contains a smaller build (about 250 KB instead of 345 KB) made for the glTF extension;
          either works.
        </li>
        <li>
          <strong>Google&apos;s CDN.</strong> Use{' '}
          <code>https://www.gstatic.com/draco/versioned/decoders/1.5.7/</code>. The Draco team asks you to use the
          versioned URLs rather than the older <code>/v1/decoders/</code> path.
        </li>
        <li>
          <strong>The default.</strong> Since r185, DRACOLoader and KTX2Loader default to the decoder files inside the three
          package, resolved relative to the loader module. Whether that works depends on your bundler, so setting the path
          explicitly is still the most predictable choice.
        </li>
      </ul>
      <p>
        The three.js docs recommend creating one DRACOLoader and reusing it, to avoid loading multiple decoder instances;
        each one runs its own pool of Web Workers (up to four by default). Call <code>dracoLoader.dispose()</code> when you
        no longer need it.
      </p>

      <h3>KTX2Loader and detectSupport</h3>
      <p>
        KTX2 textures (Basis Universal) stay compressed in GPU memory, which WebP and AVIF don&apos;t. The loader has to
        transcode them to a format the device supports, so it needs the Basis transcoder (copy{' '}
        <code>three/examples/jsm/libs/basis/</code> to your public folder) and a call to{' '}
        <code>detectSupport(renderer)</code> before loading. With <code>WebGPURenderer</code>, run{' '}
        <code>await renderer.init()</code> first; the old <code>detectSupportAsync()</code> is deprecated.
      </p>

      <h3>MeshoptDecoder</h3>
      <p>
        Meshopt files only need <code>setMeshoptDecoder(MeshoptDecoder)</code>. The decoder is a single 29 KB module with
        WebAssembly embedded, and since r183 it also handles <code>KHR_meshopt_compression</code>. For how Meshopt differs
        from Draco, see <Link to="/blog/draco-vs-meshopt">Draco vs Meshopt</Link>.
      </p>

      <h2>Show loading progress and handle errors</h2>
      <p>
        <code>loadAsync</code> accepts a progress callback that receives the browser&apos;s <code>ProgressEvent</code>, and
        rejects on failure:
      </p>
      <CodeBlock language="javascript">{`try {
  const gltf = await loader.loadAsync('/models/chair.glb', (event) => {
    if (event.lengthComputable) {
      progressBar.value = event.loaded / event.total;
    }
  });
  scene.add(gltf.scene);
} catch (error) {
  console.error('Model failed to load:', error);
}`}</CodeBlock>
      <p>
        three.js reads the total from the <code>Content-Length</code> (or <code>X-File-Size</code>) response header. If
        your server leaves it out, <code>lengthComputable</code> is false and you can only show a spinner.
      </p>
      <p>
        To track several files at once, pass a <code>LoadingManager</code> to the loader. It counts items, not bytes:
      </p>
      <CodeBlock language="javascript">{`const manager = new THREE.LoadingManager();
manager.onProgress = (url, itemsLoaded, itemsTotal) => {
  console.log(itemsLoaded + ' of ' + itemsTotal + ' loaded: ' + url);
};
manager.onLoad = () => hideLoadingScreen();
manager.onError = (url) => console.error('Error loading ' + url);

const loader = new GLTFLoader(manager);`}</CodeBlock>

      <h2>Common GLTFLoader errors and how to fix them</h2>
      <table>
        <thead>
          <tr>
            <th>Error</th>
            <th>Cause and fix</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>THREE.GLTFLoader: No DRACOLoader instance provided.</code>
            </td>
            <td>The file is Draco-compressed. Call <code>loader.setDRACOLoader(dracoLoader)</code>.</td>
          </tr>
          <tr>
            <td>
              <code>fetch for &quot;.../draco_wasm_wrapper.js&quot; responded with 404: Not Found</code>
            </td>
            <td>
              Wrong decoder path. Check that the folder exists, holds the decoder files and that the path ends in{' '}
              <code>/</code>.
            </td>
          </tr>
          <tr>
            <td>
              <code>THREE.GLTFLoader: setMeshoptDecoder must be called before loading compressed files</code>
            </td>
            <td>
              The file uses Meshopt. Call <code>loader.setMeshoptDecoder(MeshoptDecoder)</code>.
            </td>
          </tr>
          <tr>
            <td>
              <code>THREE.GLTFLoader: setKTX2Loader must be called before loading KTX2 textures</code>
            </td>
            <td>
              The file has KTX2 textures. Register a KTX2Loader, and call <code>detectSupport(renderer)</code> on it, or
              you get <code>Missing initialization with `.detectSupport( renderer )`</code>.
            </td>
          </tr>
          <tr>
            <td>
              <code>SyntaxError: Unexpected token &apos;&lt;&apos;, &quot;&lt;!DOCTYPE &quot;... is not valid JSON</code>
            </td>
            <td>
              The server returned an HTML page instead of your file, usually a 404 page or a single-page-app fallback to{' '}
              <code>index.html</code>. The loader then tries to parse HTML as glTF JSON. Check the URL in the Network tab.
              In Vite, files in <code>public/</code> are served from the root, so use <code>/model.glb</code>, not{' '}
              <code>/public/model.glb</code>. The wording differs slightly between browsers.
            </td>
          </tr>
          <tr>
            <td>
              <code>blocked by CORS policy: No &apos;Access-Control-Allow-Origin&apos; header</code>
            </td>
            <td>
              The model is on another domain (a CDN or bucket) that doesn&apos;t allow your site. Add an{' '}
              <code>Access-Control-Allow-Origin</code> header there, or serve the file from your own origin. Opening your
              page as a <code>file://</code> URL doesn&apos;t work either; use a local dev server.
            </td>
          </tr>
          <tr>
            <td>
              <code>THREE.GLTFLoader: Unsupported asset. glTF versions &gt;=2.0 are supported.</code>
            </td>
            <td>It&apos;s a glTF 1.0 file. Re-export it from your 3D tool as glTF 2.0.</td>
          </tr>
        </tbody>
      </table>
      <p>
        If the model loads without errors but you can&apos;t see it, it is often far too large or too small for your camera.
        Compute its size with <code>new THREE.Box3().setFromObject(gltf.scene)</code> and position the camera to fit.
      </p>

      <h2>React Three Fiber: useGLTF from drei</h2>
      <p>
        In React Three Fiber, use the <code>useGLTF</code> hook from <code>@react-three/drei</code>. It wraps GLTFLoader in
        R3F&apos;s <code>useLoader</code>, so results are cached by URL and the component suspends while loading:
      </p>
      <CodeBlock language="jsx">{`import { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { useGLTF, OrbitControls } from '@react-three/drei';

function Chair(props) {
  const { scene } = useGLTF('/models/chair.glb');
  return <primitive object={scene} {...props} />;
}

export default function App() {
  return (
    <Canvas camera={{ position: [0, 1, 3] }}>
      <ambientLight intensity={0.5} />
      <directionalLight position={[5, 5, 5]} />
      <Suspense fallback={null}>
        <Chair />
      </Suspense>
      <OrbitControls />
    </Canvas>
  );
}

useGLTF.preload('/models/chair.glb');`}</CodeBlock>
      <p>What to know about <code>useGLTF</code>:</p>
      <ul>
        <li>
          The signature is <code>useGLTF(path, useDraco = true, useMeshopt = true, extendLoader)</code>. Draco and Meshopt
          are on by default.
        </li>
        <li>
          The Draco decoder comes from <code>https://www.gstatic.com/draco/versioned/decoders/1.5.5/</code> by default. To
          self-host it, pass a path as the second argument or call <code>useGLTF.setDecoderPath(&apos;/draco/&apos;)</code>{' '}
          once.
        </li>
        <li>
          KTX2 is not set up for you. Use the fourth argument, which receives the loader (see below).
        </li>
        <li>
          <code>useGLTF.preload(url)</code> starts the download before the component mounts, and{' '}
          <code>useGLTF.clear(url)</code> removes it from the cache.
        </li>
        <li>
          For a progress bar, drei&apos;s <code>useProgress</code> hook returns <code>progress</code>,{' '}
          <code>active</code>, <code>loaded</code>, <code>total</code> and <code>errors</code>.
        </li>
        <li>
          A three.js object can only be in the scene once, and R3F does not dispose objects passed to{' '}
          <code>&lt;primitive&gt;</code>. To show the same model twice, clone it (drei&apos;s <code>&lt;Clone&gt;</code>)
          or use a gltfjsx component.
        </li>
      </ul>
      <p>KTX2 textures with <code>useGLTF</code>:</p>
      <CodeBlock language="jsx">{`import { useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { KTX2Loader } from 'three-stdlib';

const ktx2Loader = new KTX2Loader().setTranscoderPath('/basis/');

function Model() {
  const gl = useThree((state) => state.gl);
  const { scene } = useGLTF('/models/chair.glb', true, true, (loader) => {
    loader.setKTX2Loader(ktx2Loader.detectSupport(gl));
  });
  return <primitive object={scene} />;
}`}</CodeBlock>

      <h2>gltfjsx: turn a GLB into a React component</h2>
      <p>
        <a href="https://github.com/pmndrs/gltfjsx" target="_blank" rel="noopener noreferrer">
          gltfjsx
        </a>{' '}
        is a command-line tool that reads a glTF file and writes a JSX component that lays out every mesh, material and
        group declaratively:
      </p>
      <CodeBlock language="bash">{`npx gltfjsx chair.glb --transform --types`}</CodeBlock>
      <p>
        This writes <code>Chair.tsx</code> (<code>Chair.jsx</code> without <code>--types</code>) and{' '}
        <code>chair-transformed.glb</code> to the current folder. Move the component into <code>src/</code> and the .glb
        into <code>public/</code>. The generated component looks roughly like this:
      </p>
      <CodeBlock language="jsx">{`export function Model(props) {
  const { nodes, materials } = useGLTF('/chair-transformed.glb');
  return (
    <group {...props} dispose={null}>
      <mesh geometry={nodes.Seat.geometry} material={materials.Wood} />
      <mesh geometry={nodes.Legs.geometry} material={materials.Metal} />
    </group>
  );
}

useGLTF.preload('/chair-transformed.glb');`}</CodeBlock>
      <p>
        Because it references geometries and materials rather than the loaded scene object, you can render{' '}
        <code>&lt;Model /&gt;</code> as many times as you like, change materials per mesh, or attach events. The{' '}
        <code>dispose=&#123;null&#125;</code> stops R3F from disposing the cached geometry when the component unmounts.
      </p>

      <h3>What --transform does</h3>
      <p>
        <code>--transform</code> (<code>-T</code>) leaves your original file alone and writes a separate{' '}
        <code>[name]-transformed.glb</code> in the same folder as the generated component. Under the hood it runs a series
        of{' '}
        <Link to="/blog/gltf-transform-optimize">gltf-transform</Link> operations:
      </p>
      <ul>
        <li>
          Packs simple materials into shared palette textures, removes duplicates, flattens the node tree and joins
          compatible meshes.
        </li>
        <li>Welds vertices, resamples animations and prunes unused data.</li>
        <li>
          Caps texture size at <code>--resolution</code> (default 1024 px) and re-encodes textures in{' '}
          <code>--format</code> (default <code>webp</code>). Normal maps get a higher cap, 2048 px or your{' '}
          <code>--resolution</code> if larger, and are saved as JPEG.
        </li>
        <li>Compresses geometry with Draco, which <code>useGLTF</code> decodes automatically.</li>
      </ul>
      <p>
        Other useful flags: <code>--simplify</code> (<code>-S</code>) to reduce triangles, <code>--keepmeshes</code> and{' '}
        <code>--keepmaterials</code> to skip joining, <code>--instance</code> (<code>-i</code>) to instance repeated
        geometry, <code>--shadows</code>, and <code>--output</code> (<code>-o</code>) to choose the file name.
      </p>
      <p>
        Watch the texture resolution: 1024 px is small for hero product shots. Use <code>--resolution 2048</code> if detail
        matters.
      </p>

      <h2>Performance tips</h2>
      <ul>
        <li>
          <strong>Compress before you ship.</strong> Draco or Meshopt for geometry, plus resized WebP or AVIF textures,
          typically cuts a GLB by well over half. See{' '}
          <Link to="/blog/reduce-glb-file-size">how to reduce GLB file size</Link> for the full process.
        </li>
        <li>
          <strong>Size textures for the screen.</strong> File size and GPU memory are different things: a 4096 × 4096
          texture takes about 64 MB of GPU memory uncompressed (plus a third more for mipmaps), whether the file was a 2 MB
          JPEG or a 500 KB WebP. Only KTX2 stays compressed on the GPU.
        </li>
        <li>
          <strong>Instance repeated objects.</strong> GLTFLoader turns <code>EXT_mesh_gpu_instancing</code> into an{' '}
          <code>InstancedMesh</code>, and gltfjsx has <code>--instance</code>. Check <code>renderer.info.render.calls</code>{' '}
          to see your draw call count.
        </li>
        <li>
          <strong>Reuse loaders and decoders.</strong> One GLTFLoader, one DRACOLoader, one KTX2Loader for the whole app.
        </li>
        <li>
          <strong>Dispose what you remove.</strong> Removing a model from the scene does not free GPU memory. Call{' '}
          <code>dispose()</code> on its geometries, materials and textures (disposing a material does not dispose its
          textures), and watch <code>renderer.info.memory</code>.
        </li>
      </ul>
      <CodeBlock language="javascript">{`function disposeModel(root) {
  root.traverse((object) => {
    if (!object.isMesh) return;
    object.geometry.dispose();
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      for (const value of Object.values(material)) {
        if (value && value.isTexture) value.dispose();
      }
      material.dispose();
    }
  });
}`}</CodeBlock>
      <p>
        GLTFLoader decodes images into <code>ImageBitmap</code>s where it can. The three.js manual notes these also need{' '}
        <code>ImageBitmap.close()</code> to free their CPU-side memory.
      </p>

      <Callout title="Compress the GLB before it reaches three.js">
        Our <Link to="/">GLB compressor</Link> applies Draco, texture resizing and WebP or AVIF conversion in one pass,
        validates the output with the Khronos glTF validator, and has a <Link to="/docs/api">REST API</Link> for build
        pipelines. The Draco output loads with the <code>DRACOLoader</code> setup above or with <code>useGLTF</code> as-is.
      </Callout>

      <h2>FAQ</h2>

      <h3>What is the difference between GLTFLoader and useGLTF?</h3>
      <p>
        <code>useGLTF</code> is a React hook from drei that wraps a GLTFLoader (from three-stdlib) in React Three
        Fiber&apos;s <code>useLoader</code>. On top of the loader it adds caching by URL, Suspense support, preloading, and
        Draco and Meshopt decoding switched on by default.
      </p>

      <h3>Where do I put the Draco decoder files?</h3>
      <p>
        Copy <code>node_modules/three/examples/jsm/libs/draco/</code> into your public folder and call{' '}
        <code>setDecoderPath(&apos;/draco/&apos;)</code> with a trailing slash. Alternatively, point it at Google&apos;s
        versioned CDN URL.
      </p>

      <h3>Does gltfjsx compress my model?</h3>
      <p>
        Only with <code>--transform</code> (the <code>--instance</code> flags run the same step). It then writes a separate{' '}
        <code>-transformed.glb</code> with Draco geometry and textures capped at 1024 px WebP by default, and the generated
        component loads that file.
      </p>

      <h3>Can three.js load .gltf files as well as .glb?</h3>
      <p>
        Yes, GLTFLoader handles both. A .gltf file references separate .bin and texture files that must sit at the right
        relative paths, which is why a single .glb is easier to deploy. The free{' '}
        <Link to="/gltf-to-glb">glTF to GLB converter</Link> packs them into one file in your browser.
      </p>

      <h3>Why is my GLB model black in three.js?</h3>
      <p>
        glTF uses physically based materials, which need light. Add lights or an environment map (
        <code>scene.environment</code>, or drei&apos;s <code>&lt;Environment&gt;</code> in React Three Fiber).
      </p>
    </>
  );
}
